import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Radio, Table, Tag, Tooltip, Typography } from 'antd'
import { Link, generatePath } from 'react-router-dom'
import _ from 'lodash'
import styled from 'styled-components'

import { Queries } from 'admin/lib/Api'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'

export function UsersList({
  searchName = '',
  searchEmail = '',
  searchMemberships = ''
}) {
  const [memberships, setMemberships] = useState('accounts')
  const [userType, setUserType] = useState('all')
  const [accountStatus, setAccountStatus] = useState('all')
  const users = Queries.useUsers()
  const userAccountMappings = Queries.useUsersAccountMappings()

  const filteredUsers = useMemo(() => {
    if (!users.isFetched || !users.data) {
      return []
    }

    let list = _(users.data)

    if (accountStatus === 'enabled') {
      list = list.filter(user => user.accountEnabled)
    } else if (accountStatus === 'disabled') {
      list = list.filter(user => !user.accountEnabled)
    }

    if (userType === 'qflow') {
      list = list.filter(user =>
        user.email?.toLowerCase().endsWith('@qualisflow.com')
      )
    } else if (userType === 'non-qflow') {
      list = list.filter(
        user => !user.email?.toLowerCase().endsWith('@qualisflow.com')
      )
    }

    if (searchName) {
      list = list.filter(user =>
        user.displayName?.toLowerCase().includes(searchName.toLocaleLowerCase())
      )
    }
    if (searchEmail) {
      list = list.filter(user =>
        user.email?.toLowerCase().includes(searchEmail.toLocaleLowerCase())
      )
    }
    if (searchMemberships) {
      const searchValue = searchMemberships.toLocaleLowerCase()
      const userIds = []
      _.forOwn(userAccountMappings?.data, (accountMappings, userId) => {
        if (
          accountMappings.some(
            acc =>
              acc.name.toLowerCase().includes(searchValue) ||
              acc.projects.some(proj =>
                proj.name.toLowerCase().includes(searchValue)
              )
          )
        ) {
          userIds.push(userId)
        }
      })
      list = list.filter(user => userIds.some(uId => uId === user.userId))
    }

    return list.value()
  }, [
    users.isFetched,
    users.data,
    accountStatus,
    userType,
    searchName,
    searchEmail,
    searchMemberships,
    userAccountMappings?.data
  ])

  const columns = useMemo(
    /** @returns {import("antd/lib/table").ColumnsType<any>} */
    () => [
      {
        title: (
          <div>
            Name
            <span onClick={e => e.stopPropagation()}>
              <Radio.Group
                value={accountStatus}
                onChange={e => setAccountStatus(e.target.value)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'enabled', label: 'Enabled' },
                  { value: 'disabled', label: 'Disabled' }
                ]}
                optionType="button"
                size="small"
                style={{ marginLeft: '1rem' }}
              />
            </span>
          </div>
        ),
        dataIndex: 'displayName',
        width: '16.5rem',
        sorter: (a, b) => a.displayName?.localeCompare(b.displayName),
        defaultSortOrder: 'ascend',
        render: (displayName, user) => (
          <DisplayEnabledAndName>
            <div>
              {user.accountEnabled ? (
                <Tooltip title="User Enabled" placement="topLeft">
                  <span role="img" aria-label="User Enabled">
                    🟢
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title="User Disabled" placement="topLeft">
                  <span role="img" aria-label="User Disabled">
                    🔴
                  </span>
                </Tooltip>
              )}
            </div>
            <div>{displayName}</div>
          </DisplayEnabledAndName>
        )
      },
      {
        title: (
          <div>
            Email
            <span onClick={e => e.stopPropagation()}>
              <Radio.Group
                value={userType}
                onChange={e => setUserType(e.target.value)}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'qflow', label: 'Qflow' },
                  { value: 'non-qflow', label: 'Non-Qflow' }
                ]}
                optionType="button"
                size="small"
                style={{ marginLeft: '1rem' }}
              />
            </span>
          </div>
        ),
        dataIndex: 'email',
        width: '18rem',
        sorter: (a, b) => a.email?.localeCompare(b.email)
      },
      {
        title: 'Notifications Enabled',
        dataIndex: 'notificationsEnabled',
        width: '6.5rem',
        render: notificationsEnabled => (notificationsEnabled ? '🟢' : '🔴'),
        align: 'center'
      },
      {
        title: (
          <div>
            Memberships
            <span onClick={e => e.stopPropagation()}>
              <Radio.Group
                value={memberships}
                onChange={e => setMemberships(e.target.value)}
                options={[
                  { value: 'accounts', label: 'Accounts' },
                  { value: 'projects', label: 'Projects' },
                  { value: 'both', label: 'Both' }
                ]}
                optionType="button"
                size="small"
                style={{ marginLeft: '1rem' }}
              />
            </span>
          </div>
        ),
        key: 'memberships',
        render: user => {
          let accounts = userAccountMappings.data[user.userId]
          accounts = accounts ?? []
          return (
            <>
              {(memberships === 'accounts' || memberships === 'both') && (
                <MembershipBlock>
                  {_(accounts)
                    .sortBy(a => a.name.toLowerCase())
                    .map(({ accountId, name }) => (
                      <MembershipTag color="blue" key={accountId}>
                        {name}
                      </MembershipTag>
                    ))
                    .value()}
                </MembershipBlock>
              )}

              {(memberships === 'projects' || memberships === 'both') && (
                <MembershipBlock>
                  {_(accounts)
                    .flatMap(a => a.projects)
                    .uniqBy(p => p.projectId)
                    .sortBy(p => p.name.toLowerCase())
                    .map(({ projectId, name }) => (
                      <MembershipTag color="volcano" key={projectId}>
                        {name}
                      </MembershipTag>
                    ))
                    .value()}
                </MembershipBlock>
              )}
            </>
          )
        }
      },
      {
        title: '',
        key: 'edit',
        width: '4rem',
        render: user => (
          <Typography.Text>
            <Link
              to={generatePath(ROUTES.USERS.EDIT, {
                userId: user.userId
              })}
            >
              Edit
            </Link>
          </Typography.Text>
        )
      }
    ],
    [memberships, userAccountMappings.data, userType, accountStatus]
  )

  if (
    (users.isFetching && !users.data) ||
    (userAccountMappings.isFetching && !userAccountMappings.data)
  ) {
    return <ScreenSpin />
  }

  return (
    <Table
      columns={columns}
      dataSource={filteredUsers}
      rowKey="userId"
      pagination={{
        pageSize: 300,
        showSizeChanger: false,
        showTotal: (total, range) =>
          `Showing ${range[0]}-${range[1]} of ${total} users`
      }}
      scroll={{ y: `calc(100vh - 232px)` }}
      size="small"
      sticky
      style={{
        margin: '1rem 0.5rem 0 0.5rem'
      }}
    />
  )
}

UsersList.propTypes = {
  searchName: PropTypes.string,
  searchEmail: PropTypes.string,
  searchMemberships: PropTypes.string
}

const DisplayEnabledAndName = styled.div`
  display: flex;
  gap: 4px;
`
const MembershipBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
`
const MembershipTag = styled(Tag)`
  margin: 0;
`
